import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import Form, { FormProps } from '@/components/Form';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useFetchOrgRoles from '@/modules/corporate/modules/learners/views/LearnerManager/components/Invite/Learner/Dialog/components/ManualInvite/Editor/DepartmentRole/hooks/useFetchOrgRoles';
import { UpdateUserInput } from '@/types/schema';

import { LearnerEditProfileDetailsFragment } from '../../../graphql/queries/generated/learnerEditProfileDetails';
import {
  adaptEditProfileInputOnSubmit,
  EditProfileInput,
  FORM_LAYOUT,
  getFormFields,
  validateForm,
} from './config';

function Editor({
  setFormikSubmit,
  onSubmit,
  learner,
}: {
  setFormikSubmit: FormProps['setSubmitRef'];
  onSubmit: FormProps<UpdateUserInput>['onSubmit'];
  learner: LearnerEditProfileDetailsFragment;
}) {
  const { t } = useTranslation();
  const { fetchOrgRoles } = useFetchOrgRoles();

  const { [FeatureFlags.EnableDesignation]: isDesignationEnabled } = useFeatureFlags();

  const initialValues: EditProfileInput = useMemo(() => {
    const responsibilityIds = learner.responsibilities?.map(({ id }) => id) || [];
    return {
      name: learner.name,
      emailId: learner.emailId,
      userId: learner.id,
      reportsToUserId: learner.manager?.id,
      orgRole: { set: learner.orgRole?.id },
      department: { set: learner.department?.id },
      responsibilityIds: { initialValues: responsibilityIds, add: [...responsibilityIds] },
    };
  }, [
    learner.department?.id,
    learner.emailId,
    learner.id,
    learner.manager?.id,
    learner.name,
    learner.orgRole?.id,
    learner.responsibilities,
  ]);

  const _onSubmit = useCallback(
    (values: EditProfileInput, formikHelpers: any) => {
      const input = adaptEditProfileInputOnSubmit(values, isDesignationEnabled, learner);
      onSubmit(input, formikHelpers);
    },
    [onSubmit, isDesignationEnabled, learner],
  );

  return (
    <Form<EditProfileInput>
      fields={getFormFields(t, learner)}
      formName="UpdateLearnerEditor"
      initialValues={initialValues}
      layout={FORM_LAYOUT}
      setSubmitRef={setFormikSubmit}
      validate={validateForm(learner, fetchOrgRoles)}
      validateOnMount={false}
      onSubmit={_onSubmit}
    />
  );
}

export default Editor;
